import type { Appearance, Candidate, CandidateInput, Dashboard, SearchRun, User } from "./types";
import { getApiTargets } from "./apiConfig";

const configuredApiUrl = String(import.meta.env.VITE_API_URL || "");
const TOKEN_KEY = "careermove.pwa.session";
const API_URLS = getApiTargets(configuredApiUrl, import.meta.env.PROD);

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token: string) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  authenticated = true,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (authenticated && getToken()) headers.set("Authorization", `Bearer ${getToken()}`);
  const retryable = (
    !options.method
    || options.method === "GET"
    || ["/api/auth/login", "/api/auth/register", "/api/auth/password/forgot"].includes(path)
  );
  let lastError: unknown;
  for (let attempt = 0; attempt < API_URLS.length; attempt += 1) {
    const baseUrl = API_URLS[attempt];
    const target = `${baseUrl}${path}`;
    try {
      const response = await fetch(target, { ...options, headers });
      if (response.status === 204) return undefined as T;
      const data = await response.json().catch(() => ({}));
      if (response.ok) return data as T;
      const shouldRetry =
        retryable
        && attempt < API_URLS.length - 1
        && [404, 502, 503, 504].includes(response.status);
      if (!shouldRetry) {
        const message = typeof data.detail === "string" ? data.detail : "Сервис временно недоступен.";
        if (response.status === 401 && authenticated) setToken("");
        throw new ApiError(message, response.status);
      }
    } catch (error) {
      lastError = error;
      if (!retryable || attempt >= API_URLS.length - 1) {
        throw new ApiError(
          "Сервер временно недоступен. Проверьте соединение и повторите через несколько секунд.",
          0,
        );
      }
    }
  }
  throw new ApiError(
    lastError instanceof Error && lastError.message
      ? lastError.message
      : "Сервер временно недоступен. Проверьте соединение и повторите через несколько секунд.",
    0,
  );
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token?: string; user?: User; requires_totp?: boolean; challenge?: string }>(
      "/api/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
      false,
    ),
  register: (email: string, password: string, privacy_accepted: boolean) =>
    request<{ token: string; user: User }>(
      "/api/auth/register",
      { method: "POST", body: JSON.stringify({ email, password, privacy_accepted }) },
      false,
    ),
  forgotPassword: (email: string) =>
    request<{ message: string }>(
      "/api/auth/password/forgot",
      { method: "POST", body: JSON.stringify({ email }) },
      false,
    ),
  resetPassword: (token: string, password: string) =>
    request<{ message: string }>(
      "/api/auth/password/reset",
      { method: "POST", body: JSON.stringify({ token, password }) },
      false,
    ),
  totp: (challenge: string, code: string) =>
    request<{ token: string; user: User }>(
      "/api/auth/totp",
      { method: "POST", body: JSON.stringify({ challenge, code }) },
      false,
    ),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  me: () => request<User>("/api/me"),
  dashboard: () => request<Dashboard>("/api/dashboard"),
  createCandidate: (candidate: CandidateInput) =>
    request<Candidate>("/api/candidates", { method: "POST", body: JSON.stringify(candidate) }),
  updateCandidate: (id: number, candidate: CandidateInput) =>
    request<Candidate>(`/api/candidates/${id}`, { method: "PATCH", body: JSON.stringify(candidate) }),
  saveResume: (candidateId: number, payload: { title: string; language: "RU" | "EN" | "SR" | "OTHER"; content: string }) =>
    request<{ ok: boolean }>(`/api/candidates/${candidateId}/resumes`, { method: "POST", body: JSON.stringify(payload) }),
  transformResume: (payload: { content: string; language: "RU" | "EN" | "SR" | "OTHER" }) =>
    request<{ content: string; message: string }>("/api/resumes/transform", { method: "POST", body: JSON.stringify(payload) }),
  uploadPhoto: (candidateId: number, file: File) => {
    const form = new FormData();
    form.append("photo", file);
    return request<{ ok: boolean; photo_data: string }>(`/api/candidates/${candidateId}/photo`, { method: "POST", body: form });
  },
  prepareApplications: (vacancyIds: number[]) =>
    request<{ prepared: number[]; sent: number; message: string }>("/api/applications/prepare", { method: "POST", body: JSON.stringify({ vacancy_ids: vacancyIds }) }),
  appearance: (appearance: Appearance) =>
    request<Appearance>("/api/settings/appearance", {
      method: "PATCH",
      body: JSON.stringify({
        theme: appearance.theme,
        font_scale: appearance.font_scale,
        density: appearance.density,
      }),
    }),
  pushConfig: () =>
    request<{ enabled: boolean; public_key: string }>("/api/push/config"),
  pushSubscribe: (subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }) =>
    request<{ ok: boolean }>("/api/push/subscribe", {
      method: "POST",
      body: JSON.stringify(subscription),
    }),
  startSearch: (use_ai = true) =>
    request<{ run_id: string; status: string; detail?: string }>("/api/search", {
      method: "POST",
      body: JSON.stringify({ force: true, use_ai }),
    }),
  searchStatus: (runId: string) => request<SearchRun>(`/api/search/${runId}`),
  jobStatus: (id: number, status: string) =>
    request<{ id: number; status: string }>(`/api/jobs/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
};
